<template>
    <div class="redesign-simple-table" data-testid="simple-table-container">
        <Portal :to="`widget-header__${data.WidgetID}`">
            <div class="flex justify-start gap-2" data-testid="simple-table-controls">
                <div>
                    <VcInput
                        v-model="filteredTableInput"
                        prefix-icon="vc-icon-search"
                        data-test-name="simple-table-search"
                        :placeholder="String(t('general.search'))"
                    />
                </div>
                <div>
                    <VcSelect
                        v-model="paginationSizeModel"
                        :options="pageSizeOptions"
                        :multiple="false"
                        :filterable="false"
                        data-test-name="simple-table-page-size"
                        class="w-fit min-w-[220px]"
                        :config="{
                            labelKey: 'label',
                            valueKey: 'value'
                        }"
                    >
                        <template #footer>
                            <div class="w-40 mx-auto px-4 pb-4">
                                <span class="text-xs pb-2 text-center main-text-color">
                                    {{ t('widget.table.customValue') }}
                                </span>
                                <div
                                    class="flex gap-2"
                                >
                                    <VcInput
                                        v-model="customPaginatedValue"
                                    />
                                    <VcButton
                                        type="outline"
                                        @click="applyCustomPagination"
                                    >
                                        {{ t('general.apply') }}
                                    </VcButton>
                                </div>
                            </div>
                        </template>
                    </VcSelect>
                </div>
            </div>
            <slot name="additional-data" />
        </Portal>
        <RedesignDataTableComponent
            :columns="columns"
            :columns-config="columnsConfig"
            :table-data="paginatedData"
            :not-paginated-table-data="filteredData"
            :widget-data="data"
            :stripe="true"
            paginated
            :pagination-height="PAGINATION_HEIGHT"
            @update-widget="updateWidget"
            @update-columns-config="updateColumnsConfig"
            @update-headers-and-columns-config="updateColumnsAndHeadersConfig"
        >
            <template #cell="{ row, column }">
                <component
                    :is="getCellComponent(row[column.prop])"
                    :value="row[column.prop]"
                    :column="column"
                    :column-config="columnsConfig[column.prop]"
                />
            </template>
        </RedesignDataTableComponent>
        <div
            :style="{ height: `${PAGINATION_HEIGHT}px` }"
            class="flex items-center justify-end px-4"
        >
            <RedesignPagination
                :total-items="tableDataRef.length"
                :per-page="paginationSizeModel"
                :current-page="currentPaginationPage"
                :from="from"
                :to="to"
                @update-current-page="updateCurrentPaginationPage"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch, } from 'vue'
import { useI18n } from 'vue-i18n'
import { VcInput, VcSelect, VcButton } from '@voicenter-team/voicenter-ui-plus'
import usePaginatedData from '@/composables/usePaginatedData'
import RedesignDataTableComponent from '@/components/RedesignBaseComponents/RedesignDataTableComponent.vue'
import {
    ColumnConfig,
    ColumnsObjectConfig,
    TableHeadersConfig,
    WidgetExtended,
    WidgetTemplateColumnWithConfig
} from '@/types/dashboard'
import { hasColumnSpecificAlignment } from '@/helpers/alignmentUtils'
import RedesignPagination from '@/components/RedesignBaseComponents/RedesignPagination.vue'
import BooleanCell from '@/components/RedesignComponents/RedesignTableComponents/BooleanCell.vue'
import DefaultCell from '@/components/RedesignComponents/RedesignTableComponents/DefaultCell.vue'
import ObjectListCell from '@/components/RedesignComponents/RedesignTableComponents/ObjectListCell.vue'
import StringsListCell from '@/components/RedesignComponents/RedesignTableComponents/StringsListCell.vue'
import JsonCell from '@/components/RedesignComponents/RedesignTableComponents/jsonCell.vue'
import { sortTableDataByDefaultList } from '@/helpers/tableSortingUtils'

type TableRow = Record<string, string | number>

const PAGINATION_HEIGHT = 40

const props = withDefaults(
    defineProps<{
        columns: WidgetTemplateColumnWithConfig[]
        tableData: TableRow[]
        data: WidgetExtended
        columnsConfig: Record<string, ColumnConfig>
    }>(),
    {
        columns: () => [],
        tableData: () => [],
    }
)

const { t } = useI18n()

const emit = defineEmits<{
    (event: 'update-widget', payload: WidgetExtended): void
}>()

const pageSizeOptions = computed(() => [ 5, 10, 20, 50, 100, 500 ].map((size) => ({
    label: `${size} ${t('general.perpage')}`,
    value: size
})))

const defaultValue = 10
/*Data*/
const filteredTableInput = ref('')
const tableDataRef = toRef(props, 'tableData')
const paginationSizeModel = ref(props.data.WidgetLayout?.paginationSize || defaultValue)
const customPaginatedValue = ref(props.data.WidgetLayout?.paginationSize || defaultValue)
const currentPaginationPage = ref(1)

//Computed
const from = computed(() => start.value + 1)
const to = computed(() => end.value > tableDataRef.value.length ? tableDataRef.value.length : end.value)

const sortedTableData = computed(() =>
    sortTableDataByDefaultList(tableDataRef.value, props.data)
)

const filteredData = computed(() => {
    const data = sortedTableData.value

    if (filteredTableInput.value) {
        const lowerFilter = filteredTableInput.value.toLowerCase()
        return data.filter((row) =>
            Object.values(row).some(field =>
                field?.toString().toLowerCase().includes(lowerFilter)
            )
        )
    }
    return data
})

const { paginatedData, start, end } = usePaginatedData(filteredData, paginationSizeModel, currentPaginationPage)

/*Methods*/
const updateColumnsConfig = (config: ColumnsObjectConfig) => {
    if (!config || !Object.values(config).length) {
        return
    }
    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)
    const updatedWidgetData = {
        ...props.data,
        WidgetLayout: {
            ...(props.data.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            columnsObjectConfig: config
        }
    }
    updateWidget(updatedWidgetData)
}
const updateColumnsAndHeadersConfig = (config: ColumnsObjectConfig, tableHeadersConfig: TableHeadersConfig) => {
    const hasColumnSpecificAlignmentValue = hasColumnSpecificAlignment(config, props.data.WidgetLayout?.tableColumnsAlignment)
    const updatedWidgetData = {
        ...props.data,
        WidgetLayout: {
            ...(props.data.WidgetLayout || {}),
            hasColumnSpecificAlignment: hasColumnSpecificAlignmentValue,
            ...(config && Object.values(config) ? { columnsObjectConfig: config } : {}),
            ...(tableHeadersConfig ? { tableHeadersConfig } : {})
        }
    }
    updateWidget(updatedWidgetData)
}
const updateCurrentPaginationPage = (value: number) => {
    currentPaginationPage.value = value
}
const applyCustomPagination = () => {
    if (customPaginatedValue.value) {
        paginationSizeModel.value = +customPaginatedValue.value
    }
}

const updateWidget = (newData: WidgetExtended) => {
    emit('update-widget', newData)
}
function isArrayOfObjects (value: unknown) {
    return Array.isArray(value) && value.every(v => v && typeof v === 'object')
}

function isJsonCell (value: unknown) {
    if (typeof value !== 'string') return false
    try {
        const parsed = JSON.parse(value)
        return typeof parsed === 'object' && parsed !== null
    } catch {
        return false
    }
}

function isArrayOfStrings (value: unknown) {
    return Array.isArray(value) && value.every(v => typeof v === 'string')
}

function getCellComponent (value: unknown) {
    if (typeof value === 'boolean') return BooleanCell
    if (isArrayOfObjects(value)) return ObjectListCell
    if (isArrayOfStrings(value)) return StringsListCell
    if (isJsonCell(value)) return JsonCell
    return DefaultCell
}

watch(() => paginationSizeModel.value, (newVal, oldValue) => {
    if (newVal && newVal !== oldValue) {
        customPaginatedValue.value = newVal
        updateCurrentPaginationPage(1)
        const updatedWidgetData = {
            ...props.data,
            WidgetLayout: {
                ...props.data.WidgetLayout,
                paginationSize: newVal
            }
        }
        updateWidget(updatedWidgetData)
    }
})

watch(() => props.data, (val) => {
    if (val) {
        const newPaginationSize = val.WidgetLayout?.paginationSize || defaultValue
        paginationSizeModel.value = newPaginationSize
        customPaginatedValue.value = newPaginationSize
    }
}, {
    deep: true
})
</script>

<style lang="scss" scoped>
</style>

